// AI 气泡 markdown 渲染扩展：在 markdown-it 之上补齐接龙场景需要的语法。
// 零依赖（markdown-it 实例由调用方传入），便于 test/aiPublishUtils.test.mjs 直接加载。
// 能力范围对齐小程序侧 utils/markdownRichText.js：
//   ==高亮== → <mark>、++下划线++ → <ins>、下标 H~2~O、上标 x^2^、
//   行内/块级公式降级为代码样式、任务列表 [ ]/[x] 渲染为只读 checkbox。
// 安全基线（html:false、危险协议拦截）由 markdown-it 默认行为保证，见 wrapper 配置。

const WHITESPACE_RE = /\s/;

function isWhitespace(ch) {
  return WHITESPACE_RE.test(ch);
}

// 单字符定界符的行内规则：`x内容x` 形式，内容非空、不含换行、两端不是空白。
// 双写定界符（如 ~~ 删除线）交给 markdown-it 内置规则，这里直接跳过。
function createSingleCharDelimiterRule(tag, chCode) {
  const markup = String.fromCharCode(chCode);
  return function (state, silent) {
    const src = state.src;
    const pos = state.pos;
    if (src.charCodeAt(pos) !== chCode) return false;
    if (src.charCodeAt(pos + 1) === chCode) return false;
    const end = src.indexOf(markup, pos + 1);
    if (end === -1 || end >= state.posMax) return false;
    const content = src.slice(pos + 1, end);
    if (!content || content.indexOf("\n") !== -1) return false;
    if (isWhitespace(content.charAt(0)) || isWhitespace(content.charAt(content.length - 1))) {
      return false;
    }
    if (silent) return true;

    let token = state.push(tag + "_open", tag, 1);
    token.markup = markup;
    token = state.push("text", "", 0);
    token.content = content;
    token = state.push(tag + "_close", tag, -1);
    token.markup = markup;
    state.pos = end + 1;
    return true;
  };
}

// 行内公式 $..$ 降级为代码样式。比普通定界符多两条防误判：
// 结束 $ 后紧跟数字时视为货币文本（如「$20 和 $30」）；内容两端不能是空白。
function inlineFormulaRule(state, silent) {
  const src = state.src;
  const pos = state.pos;
  if (src.charCodeAt(pos) !== 0x24 /* $ */) return false;
  const end = src.indexOf("$", pos + 1);
  if (end === -1 || end >= state.posMax) return false;
  const content = src.slice(pos + 1, end);
  if (!content || content.indexOf("\n") !== -1) return false;
  if (isWhitespace(content.charAt(0)) || isWhitespace(content.charAt(content.length - 1))) {
    return false;
  }
  if (/[0-9]/.test(src.charAt(end + 1))) return false;
  if (silent) return true;

  let token = state.push("code_open", "code", 1);
  token.markup = "$";
  token.attrSet("class", "ai-md-formula");
  token = state.push("text", "", 0);
  token.content = content;
  token = state.push("code_close", "code", -1);
  token.markup = "$";
  state.pos = end + 1;
  return true;
}

// 块级公式：整行 $$ 开始、整行 $$ 结束，中间内容按代码样式展示。
function mathBlockRule(state, startLine, endLine, silent) {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];
  if (pos + 2 > max) return false;
  if (state.src.charCodeAt(pos) !== 0x24 /* $ */ || state.src.charCodeAt(pos + 1) !== 0x24) {
    return false;
  }
  // 首行 $$ 之后必须无内容（块级公式独立成段）
  if (state.src.slice(pos + 2, max).trim()) return false;
  if (silent) return true;

  const lines = [];
  let nextLine = startLine;
  for (;;) {
    nextLine += 1;
    if (nextLine >= endLine) break;
    pos = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];
    const lineText = state.src.slice(pos, max);
    if (lineText.trim() === "$$") {
      const token = state.push("math_block", "code", 0);
      token.content = lines.join("\n");
      token.markup = "$$";
      token.map = [startLine, nextLine + 1];
      state.line = nextLine + 1;
      return true;
    }
    lines.push(lineText);
  }
  // 未闭合的 $$：不消费任何行，回落给后续规则按普通段落处理
  return false;
}

// 紧凑列表的任务项：`<li>[ ] 待办` / `<li>[x] 已完成` → 只读 checkbox。
// 松散列表（<li><p>…）保持字面文本，属可接受降级。
function postProcessTaskListItems(html) {
  return html.replace(/<li>\[([ xX])\] /g, (match, flag) => {
    const checked = flag === " " ? "" : " checked";
    return `<li class="ai-md-task"><input type="checkbox" disabled${checked}> `;
  });
}

export function applyAiMarkdownExtensions(md) {
  md.inline.ruler.before(
    "emphasis",
    "ai_mark",
    createSingleCharDelimiterRule("mark", 0x3d /* = */),
  );
  md.inline.ruler.before(
    "emphasis",
    "ai_ins",
    createSingleCharDelimiterRule("ins", 0x2b /* + */),
  );
  md.inline.ruler.before(
    "emphasis",
    "ai_sub",
    createSingleCharDelimiterRule("sub", 0x7e /* ~ */),
  );
  md.inline.ruler.before(
    "emphasis",
    "ai_sup",
    createSingleCharDelimiterRule("sup", 0x5e /* ^ */),
  );
  md.inline.ruler.before("emphasis", "ai_formula", inlineFormulaRule);

  md.block.ruler.before("fence", "ai_math_block", mathBlockRule, { alt: [] });

  md.renderer.rules.math_block = function (tokens, idx) {
    const token = tokens[idx];
    return (
      '<pre class="ai-md-math"><code>' +
      md.utils.escapeHtml(token.content) +
      "</code></pre>"
    );
  };

  const originalRender = md.render.bind(md);
  md.render = function (src) {
    return postProcessTaskListItems(originalRender(src));
  };

  return md;
}

export default {
  applyAiMarkdownExtensions,
};
